"use client";

import { FieldProps } from "@/_components/lib/ui/field";
import {
  Box,
  Field as ChakraField,
  createListCollection,
  Portal,
  Select,
  Text,
  useControllableState,
} from "@chakra-ui/react";
import { Field } from "@/_components/lib/ui/field";
import { useEffect, useId, useState, ReactNode } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { FormErrorMessage } from "./_formErrMessage";
import { get } from "lodash";
import {
  sharedFieldStyles,
  sharedLabelFloatingStyles,
  formFieldFloatingWrapperStyles,
  formFieldOutlinedWrapperStyles,
  formSelectContentStyles,
  formSelectTriggerStyles,
  formSelectValueColor,
} from "./_sharedFieldStyles";

interface FloatingLabelSelectProps {
  label: ReactNode;
  listItems: { label: string; value: string }[];
  value?: string[];
  defaultValue?: string[];
  onValueChange?: (value: string[]) => void;
  onBlur?: () => void;
  placeholder?: string;
  variant?: "subtle" | "outline";
  size?: "sm" | "md" | "lg" | "xs";
  rounded?: "none" | "sm" | "md" | "lg" | "xl" | "2xl" | "full";
  additionalItems?: (
    items: { label: string; value: string }[],
  ) => ReactNode | ReactNode[];
  disabled?: boolean;
  isRequired?: boolean;
  floatingLabelProps?: React.ComponentProps<typeof ChakraField.Label>;
  forceClose?: boolean;
}

const normalizeToArray = (
  input: string[] | string | undefined,
  fallback: string[] | undefined,
) => {
  if (Array.isArray(input)) return input;
  if (input) return [input];
  return fallback;
};

const FloatingLabelSelect = (props: FloatingLabelSelectProps) => {
  const {
    label,
    listItems,
    onValueChange,
    value,
    defaultValue = [],
    onBlur,
    placeholder,
    variant,
    size,
    rounded = "xl",
    additionalItems,
    disabled,
    isRequired,
    floatingLabelProps,
    forceClose,
  } = props;

  const collections = createListCollection({
    items: listItems,
  });

  const normalizedValue = normalizeToArray(value, undefined);
  const normalizedDefaultValue = normalizeToArray(defaultValue, []);

  const [selectState, setSelectState] = useControllableState({
    defaultValue: normalizedDefaultValue,
    onChange: onValueChange,
    value: normalizedValue,
  });

  const [focused, setFocused] = useState(false);
  const [open, setOpen] = useState(false);
  const shouldFloat =
    (Array.isArray(selectState) && selectState.length > 0) || focused;
  const hasValue = Array.isArray(selectState) && selectState.length > 0;

  useEffect(() => {
    if (!forceClose || !open) return;
    setOpen(false);
    setFocused(false);
  }, [forceClose, open]);

  return (
    <Box pos="relative" w="full">
      <Select.Root
        collection={collections}
        size={size}
        variant={variant}
        value={selectState}
        open={open}
        onValueChange={({ value }) => {
          setSelectState(value);
          onValueChange?.(value);
        }}
        onInteractOutside={() => {
          onBlur?.();
          setFocused(false);
        }}
        onOpenChange={(details) => {
          setOpen(details.open);
          setFocused(details.open);
        }}
        disabled={disabled}
        fontFamily="body"
      >
        <Select.HiddenSelect />
        <Select.Control>
          <Select.Trigger
            rounded={rounded || sharedFieldStyles.rounded}
            border={sharedFieldStyles.border}
            fontWeight={sharedFieldStyles.fontWeight}
            h={10}
            _disabled={sharedFieldStyles._disabled}
            css={formSelectTriggerStyles}
            // Prevents accidental form submit when used inside <form>
            type="button"
          >
            <Select.ValueText
              placeholder={placeholder}
              color={formSelectValueColor(hasValue)}
            />
          </Select.Trigger>
          <Select.IndicatorGroup>
            <Select.Indicator />
          </Select.IndicatorGroup>
        </Select.Control>
        <ChakraField.Label
          id="label-floating"
          css={sharedLabelFloatingStyles}
          data-float={shouldFloat || undefined}
          {...floatingLabelProps}
        >
          {label}
          {isRequired && (
            <Text as="span" color="themeColor.form.required" mr={0.5}>
              *
            </Text>
          )}
        </ChakraField.Label>
        <Portal>
          <Select.Positioner>
            <Select.Content
              fontFamily="body"
              zIndex={2000}
              css={formSelectContentStyles}
            >
              {collections.items.map((col) => (
                <Select.Item item={col} key={col.value}>
                  {col.label}
                  <Select.ItemIndicator />
                </Select.Item>
              ))}
              {additionalItems?.(collections.items)}
            </Select.Content>
          </Select.Positioner>
        </Portal>
      </Select.Root>
    </Box>
  );
};

interface SimpleSelectProps {
  variant?: "subtle" | "outline";
  size?: "sm" | "md" | "lg" | "xs";
  listItems: { label: string; value: string }[];
  name: string;
  value: string[];
  onChange: (value: string[]) => void;
  onBlur?: () => void;
  placeholder?: string;
  label?: ReactNode;
  invalid?: boolean;
  rounded?: "none" | "sm" | "md" | "lg" | "xl" | "2xl" | "full";
  additionalItems?: (
    items: { label: string; value: string }[],
  ) => ReactNode | ReactNode[];
  height?: number;
}

export const SimpleSelect = (props: SimpleSelectProps) => {
  const {
    variant,
    size,
    listItems,
    name,
    value,
    onBlur,
    onChange,
    placeholder,
    label,
    invalid,
    rounded = "md",
    additionalItems,
    height = 10,
  } = props;
  const collections = createListCollection({
    items: listItems,
  });
  const selectRootId = useId();
  const hasValue = value.length > 0;

  return (
    <Select.Root
      collection={collections}
      size={size}
      variant={variant}
      name={name}
      value={value}
      onValueChange={({ value }) => onChange(value)}
      onInteractOutside={() => onBlur?.()}
      invalid={invalid}
      id={`simple-select-${name}-${selectRootId.replace(/:/g, "")}`}
    >
      <Select.HiddenSelect />
      <Select.Label textStyle="xs" fontWeight="light" fontFamily="body">
        {label}
      </Select.Label>
      <Select.Control>
        <Select.Trigger
          rounded={rounded || sharedFieldStyles.rounded}
          border={sharedFieldStyles.border}
          fontWeight={sharedFieldStyles.fontWeight}
          h={height}
          _disabled={sharedFieldStyles._disabled}
          css={formSelectTriggerStyles}
          // Prevents accidental form submit when used inside <form>
          type="button"
        >
          <Select.ValueText
            placeholder={placeholder}
            color={formSelectValueColor(hasValue)}
          />
        </Select.Trigger>
        <Select.IndicatorGroup>
          <Select.Indicator />
        </Select.IndicatorGroup>
      </Select.Control>
      <Portal>
        <Select.Positioner>
          <Select.Content
            fontFamily="body"
            zIndex={2000}
            css={formSelectContentStyles}
          >
            {collections.items.map((col) => (
              <Select.Item item={col} key={col.value}>
                {col.label}
                <Select.ItemIndicator />
              </Select.Item>
            ))}
            {additionalItems?.(collections.items)}
          </Select.Content>
        </Select.Positioner>
      </Portal>
    </Select.Root>
  );
};

interface SimpleSelectFieldProps
  extends Omit<SimpleSelectProps, "value" | "onChange" | "onBlur" | "name"> {
  name: string;
  labelBuilder?: () => ReactNode;
  isRequired?: boolean;
  helperText?: string;
  fieldStyles?: FieldProps;
  isDisabled?: boolean;
  isFloating?: boolean;
  floatingLabelProps?: React.ComponentProps<typeof ChakraField.Label>;
  forceClose?: boolean;
}

export const SimpleSelectField = (props: SimpleSelectFieldProps) => {
  const { control, formState } = useFormContext();
  const { name, isFloating = false, ...restProps } = props;

  const labelComponent = restProps.labelBuilder
    ? restProps.labelBuilder()
    : restProps.label;

  if (isFloating) {
    const error = get(formState.errors, name);
    return (
      <ChakraField.Root
        invalid={!!error}
        {...restProps.fieldStyles}
        gap={1}
        pt={2.5}
        px={0}
        {...formFieldFloatingWrapperStyles}
      >
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <FloatingLabelSelect
              label={labelComponent}
              listItems={restProps.listItems}
              value={field.value}
              onValueChange={field.onChange}
              onBlur={field.onBlur}
              placeholder={restProps.placeholder}
              variant={restProps.variant}
              size={restProps.size}
              rounded={restProps.rounded}
              additionalItems={restProps.additionalItems}
              disabled={restProps.isDisabled}
              isRequired={restProps.isRequired}
              floatingLabelProps={restProps.floatingLabelProps}
              forceClose={restProps.forceClose}
            />
          )}
        />
        {restProps.helperText && !error && (
          <ChakraField.HelperText textStyle="xs" ml={3} display="none">
            {restProps.helperText}
          </ChakraField.HelperText>
        )}
        {error && (
          <ChakraField.ErrorText ml={3}>
            <FormErrorMessage errors={formState.errors} name={name} />
          </ChakraField.ErrorText>
        )}
      </ChakraField.Root>
    );
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const error = get(formState.errors, name);
        return (
          <Field
            {...restProps.fieldStyles}
            invalid={!!error}
            errorText={
              <FormErrorMessage errors={formState.errors} name={name} />
            }
            bg={restProps.fieldStyles?.bg || "themeColor.form.bgSolid"}
            {...formFieldOutlinedWrapperStyles}
          >
            <SimpleSelect
              {...restProps}
              name={name}
              value={field.value}
              onChange={(value) => field.onChange(value)}
              onBlur={field.onBlur}
            />
          </Field>
        );
      }}
    />
  );
};
